import React from "react";
import moment from "moment";
// Billing Details Modal

// Default
import { useState } from "react";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import CustomChip from "../chip/Chip";
import CustomDataTable from "../table/CustomTable";
import { formatDate } from "../../utils/helper";
import LottieLoader from "../animatedLoader/LottieLoader";
import useMeterDeviceEvents from "../../hooks/useMeterDeviceEvents";

// Svg
import Download from "../../assets/svg/download";

const BillingDetailsModal = ({
  openModal,
  setOpenModal,
  selectedBillingDeterminant,
  setSelectedBillingDeterminant,
  selectedBillingData,
}: {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  selectedBillingDeterminant: any;
  setSelectedBillingDeterminant: (determinant: any) => void;
  selectedBillingData: any;
}) => {
  const [loader, setLoader] = useState(false);

  const downloadCSV = () => {
    try {
      setLoader(true);
      const headers = Object.keys(selectedBillingData[0]).join(",");
      const rows = selectedBillingData
        .map((row: any) => Object.values(row).join(","))
        .join("\n");
      const csvContent = `${headers}\n${rows}`;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Generate_billing_determinant.csv`;
      link.click();

      URL.revokeObjectURL(link.href);
    } catch (error) {
      return error;
    } finally {
      setLoader(false);
    }
  };

  const { data: meterEvents = [] } = useMeterDeviceEvents(
    selectedBillingData?.[0]?.registerType || "",
  );

  const calculatePenalty = (meterId: string) => {
    if (
      !selectedBillingDeterminant?.fromDate ||
      !selectedBillingDeterminant?.toDate
    )
      return 0;

    const startTs = moment(selectedBillingDeterminant.fromDate).valueOf();
    const endTs = moment(selectedBillingDeterminant.toDate).valueOf();

    return meterEvents.filter((event: any) => {
      const eventTs = moment(event.CreatedAt).valueOf();
      return (
        event.Command === "METER_COVER_OPEN" &&
        eventTs >= startTs &&
        eventTs <= endTs
      );
    }).length;
  };

  const rowsContent = selectedBillingData?.map((item: any, index: number) => {
    const penaltyCount = calculatePenalty(item.registerType);
    const penaltyAmount = penaltyCount * 50;
    const unitsConsumed = Number(item?.qu) - Number(item?.ut);
    const totalCost = penaltyAmount + Number(item?.qt);

    return (
      <TableRow
        key={index}
        sx={{
          height: "50px",
          background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
        }}
        className="custom_table_tbody"
      >
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item?.seqNo}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item?.registerType}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item?.activeEnergy}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item?.qu}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item?.ut}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {`${penaltyAmount} (${penaltyCount} * 50)`}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {unitsConsumed.toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {item?.qt}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography className="custom_table_tbody_cell">
            {totalCost.toFixed(2)}
          </Typography>
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Dialog
      onClose={() => {
        setOpenModal(false);
        setSelectedBillingDeterminant(null);
      }}
      open={openModal}
      fullWidth
      maxWidth={"md"}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Billing Determinant Details
        </DialogTitle>
        <IconButton
          onClick={() => {
            setOpenModal(false);
            setSelectedBillingDeterminant(null);
          }}
          sx={{ marginRight: "10px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          {selectedBillingDeterminant && (
            <Stack direction={"row"} gap={"5px"} alignItems={"center"}>
              <Typography variant="body1">Status:</Typography>
              <CustomChip
                label={"Active"}
                backgroundColor={"#9CE976"}
                color={"#000000"}
              />
            </Stack>
          )}
          {loader ? (
            <LottieLoader />
          ) : (
            <IconButton onClick={downloadCSV}>
              <Download />
            </IconButton>
          )}
        </Stack>

        {selectedBillingDeterminant && (
          <Typography variant="body1" fontWeight={600} mt={"10px"}>
            Bill Period From:{" "}
            <Typography fontWeight={400}>
              {formatDate(
                new Date(selectedBillingDeterminant?.fromDate),
                "DD/MM/YYYY HH:mm:ss",
              )}
            </Typography>{" "}
          </Typography>
        )}

        {selectedBillingDeterminant && (
          <Typography variant="body1" fontWeight={600} mt={"10px"}>
            Bill Period To:{" "}
            <Typography fontWeight={400}>
              {formatDate(
                new Date(selectedBillingDeterminant?.toDate),
                "DD/MM/YYYY HH:mm:ss",
              )}
            </Typography>{" "}
          </Typography>
        )}

        <Stack mt={"20px"}>
          <CustomDataTable
            sx={{ maxHeight: "400px" }}
            columns={[
              "Seq No",
              "Meter ID",
              "Active Energy Import Total",
              "Max",
              "Min",
              "Penalty",
              "Total consumption",
              "Tariff",
              "Total Cost",
            ]}
            rowsContent={rowsContent}
            Actions={false}
          />
        </Stack>
      </DialogContent>
      {/* <DialogActions>
        <ContainedButton
          label={"Close"}
          
          sx={{
            fontWeight: 600,
            color: "#ffffff",
          }}
          onClick={() => {
            setOpenModal(false);
            setSelectedBillingDeterminant(null);
          }}
        />
      </DialogActions> */}
    </Dialog>
  );
};

export default BillingDetailsModal;
